import StaticPageLayout from '../../components/layouts/StaticPageLayout';
import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../store/store';
import GeneralError from '../../components/errors/GeneralError';
import GeneralLoader from '../../components/loaders/GeneralLoader';
import NoResults from './components/NoResults';
import WatchlistCompanyResult from './components/WatchlistCompanyResult';
import useHistory from '../../hooks/useHistory';
import {
  watchlistPagesize,
  decodeWatchlistParams,
  encodeWatchlistParams,
  watchlistSortOptions
} from './constants';
import { paths } from '../../utils/constants/routes';
import Pagination from '../../components/navigation/Pagination';
import { useEffect, useRef } from 'react';
import CompanyDetails from '../../components/profiler/CompanyDetails/CompanyDetails';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../components/buttons/IconButton';
import { ThreeDots } from 'react-loader-spinner';
import { useCompanyNavigationStack } from '../../hooks/useCompanyNavigationStack';
import { eventWatchlistViewed } from '../../ga4/ga4';

const ProfilerWatchList = observer(() => {
  const { profilerProjectStore, utilsStore, authStore } = useStore();
  const { location, navigate } = useHistory();
  const navStack = useCompanyNavigationStack({ scrollKey: null });
  const state = useLocalObservable(() => ({
    isRendered: true,
    setIsRendered: (isRendered = false) => (state.isRendered = isRendered),
    params: {},
    setParams: (params = {}) => {
      state.params = params;
    },
    get layout() {
      return utilsStore.windowWidth < 1201 ? 'mobile' : 'desktop';
    },
    isAddRemoveWatchlistPopupOpen: null,
    setIsAddRemoveWatchlistPopupOpen: (company = null) => {
      const open = !state.isAddRemoveWatchlistPopupOpen;
      state.isAddRemoveWatchlistPopupOpen = state.isAddRemoveWatchlistPopupOpen ? null : company;
      utilsStore.lockScroll(open);
    },
    get watchlist() {
      const list = profilerProjectStore.watchlist.slice();

      if (state.selectedSort) {
        if (state.selectedSort === 'name-asc') {
          list.sort((a, b) =>
            (a.name || a.companyName || '').localeCompare(b.name || b.companyName || '', 'en', {
              sensitivity: 'base'
            })
          );
        } else if (state.selectedSort === 'name-desc') {
          list.sort((a, b) =>
            (b.name || b.companyName || '').localeCompare(a.name || a.companyName || '', 'en', {
              sensitivity: 'base'
            })
          );
        } else {
          const [propKey, typeSort] = state.selectedSort.split('-');
          const sorts = {
            asc: (propKey) => (a, b) =>
              +(a[propKey] ?? Number.MAX_SAFE_INTEGER) - +(b[propKey] ?? Number.MAX_SAFE_INTEGER),
            desc: (propKey) => (a, b) =>
              +(b[propKey] ?? -Number.MAX_SAFE_INTEGER / 3) -
              +(a[propKey] ?? -Number.MAX_SAFE_INTEGER / 3)
          };
          list.sort(sorts[typeSort](propKey));
        }
      }

      return list;
    },
    selectedSort: '',
    selectSortOption: (value = '') => {
      state.selectedSort = value;
      navigate(
        paths.PROFILER_WATCH_LIST +
          '?params=' +
          encodeWatchlistParams({ ...state.params, page: 1 }),
        { replace: true }
      );
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    },
    isSortToggled: false,
    toggleSort: () => (state.isSortToggled = !state.isSortToggled)
  }));

  useEffect(() => {
    return () => {
      state.setIsRendered();
    };
  }, [state]);

  useEffect(() => {
    document.title = `transact.digital GmbH - My watchlist`;
  }, []);

  const watchlistViewedRef = useRef(false);
  useEffect(() => {
    if (
      !watchlistViewedRef.current &&
      !profilerProjectStore.initialLoadWatchlist &&
      !profilerProjectStore.isLoadingWatchlist
    ) {
      watchlistViewedRef.current = true;
      eventWatchlistViewed(profilerProjectStore.watchlist.length);
    }
  }, [
    profilerProjectStore.initialLoadWatchlist,
    profilerProjectStore.isLoadingWatchlist,
    profilerProjectStore.watchlist.length
  ]);

  useEffect(() => {
    return () => {
      if (navStack.detailsDisplayed) {
        navStack.displayDetails(null, true);
      }
      if (state.isAddRemoveWatchlistPopupOpen) {
        state.setIsAddRemoveWatchlistPopupOpen();
      }
    };
  }, [navStack, state]);

  useEffect(() => {
    if (profilerProjectStore.isLoadingWatchlist || profilerProjectStore.initialLoadWatchlist) {
      return;
    }

    const newParams = {};
    let [urlParams, params, shouldNavigate] = [null, {}, false];

    try {
      urlParams = new URLSearchParams(location.search);
      params = decodeWatchlistParams(urlParams.get('params'));
    } catch {
      shouldNavigate = true;
    }

    if (typeof params !== 'object' || params === null || params === undefined) {
      params = {};
    }

    let listPages = Math.ceil(state.watchlist.length / watchlistPagesize);
    if (listPages === 0) {
      listPages = 1;
    }

    if (isNaN(params.page) || params.page > listPages || params.page < 1) {
      if (isNaN(params.page) || params.page < 1) {
        newParams.page = 1;
      } else if (params.page > listPages) {
        newParams.page = listPages;
      }
      shouldNavigate = true;
    }

    if (shouldNavigate) {
      navigate(
        paths.PROFILER_WATCH_LIST + '?params=' + encodeWatchlistParams({ ...params, ...newParams }),
        { replace: true }
      );
    }

    state.setParams({ ...params, ...newParams });
  }, [
    state,
    profilerProjectStore,
    state.watchlist,
    state.watchlist.length,
    profilerProjectStore.isLoadingWatchlist,
    location.search,
    navigate
  ]);

  useEffect(() => {
    const handleClickOutsideSort = (event) => {
      const isClickInsideSortMenu = event.target.closest('.sort-menu-wrap');
      const isClickInsideSortButtonDesktop = event.target.closest('#sort-btn-desktop');
      const isClickInsideSortButtonMobile = event.target.closest('#sort-btn-mobile');

      if (
        isClickInsideSortMenu ||
        isClickInsideSortButtonDesktop ||
        isClickInsideSortButtonMobile
      ) {
        // do nothing
      } else {
        if (state.isSortToggled) {
          state.toggleSort();
        }
      }
    };

    document.addEventListener('click', handleClickOutsideSort);

    return () => {
      document.removeEventListener('click', handleClickOutsideSort);
    };
  }, [state]);

  const showError =
    profilerProjectStore.loadWatchlistError && !profilerProjectStore.watchlist.length;

  if (showError) {
    return (
      <StaticPageLayout page="profiler-watch-list">
        {showError && (
          <GeneralError
            errorMessage={profilerProjectStore.loadWatchlistError}
            actionMessage="You may want to:"
            withHomePageButton={true}
            actionButtontext="Try again"
            onActionButtonClick={profilerProjectStore.loadWatchlist}
            withLogoutButton
          />
        )}
      </StaticPageLayout>
    );
  }

  if (
    profilerProjectStore.initialLoadWatchlist ||
    (profilerProjectStore.isLoadingWatchlist && !profilerProjectStore.watchlist.length)
  ) {
    return (
      <StaticPageLayout page="profiler-watch-list">
        <GeneralLoader />
      </StaticPageLayout>
    );
  }

  const showNoResults = !state.watchlist.length;
  const showResults = !showNoResults;
  const showPagination = state.watchlist.length > watchlistPagesize;
  const pages = Math.ceil(state.watchlist.length / watchlistPagesize) || 1;
  const page = (state.params.page || 1) - 1;

  const companyToRemove = state.isAddRemoveWatchlistPopupOpen;

  const sortJSX = (
    <>
      <IconButton
        id={state.layout === 'desktop' ? 'sort-btn-desktop' : 'sort-btn-mobile'}
        icon={
          state.selectedSort
            ? ICON_BUTTON_ICONS.OCTI_LIST_ORDERED
            : ICON_BUTTON_ICONS.OCTI_LIST_UNORDERED
        }
        type={ICON_BUTTON_TYPES.BLUE}
        tooltipText={
          state.isSortToggled
            ? ''
            : !(
                  profilerProjectStore.isLoadingWatchlist ||
                  profilerProjectStore.initialLoadWatchlist
                ) && showResults
              ? !state.selectedSort
                ? 'Sort'
                : watchlistSortOptions.find((o) => o.value === state.selectedSort)?.name || 'Sort'
              : 'Sort'
        }
        tooltipPosition={
          state.layout === 'mobile' &&
          watchlistSortOptions.find((o) => o.value === state.selectedSort)?.name
            ? 'bottom-end'
            : 'bottom-center'
        }
        disabled={
          profilerProjectStore.isLoadingWatchlist ||
          profilerProjectStore.initialLoadWatchlist ||
          !showResults
        }
        onClick={state.toggleSort}
        filled={state.isSortToggled || !!state.selectedSort}
      />
      {state.isSortToggled && (
        <div className="sort-menu-wrap">
          {watchlistSortOptions.map((option) => {
            return (
              <IconButton
                key={option.value}
                hoverType={ICON_BUTTON_TYPES.BLUE}
                withBorder={false}
                innerText={option.shortName || option.name}
                onClick={() => state.selectSortOption(option.value) & state.toggleSort()}
                disabled={state.selectedSort === option.value}
                filled={state.selectedSort === option.value}
                {...(option.icon ? { iconOnRight: true, icon: option.icon } : {})}
              />
            );
          })}
        </div>
      )}
    </>
  );

  if (navStack.detailsDisplayed) {
    return (
      <StaticPageLayout page="profiler-watch-list">
        <CompanyDetails
          company={navStack.detailsDisplayed}
          displayDetails={navStack.displayDetails}
          goBackToPreviousCompany={navStack.goBackToPreviousCompany}
          companiesStackLength={navStack.companiesStack.length}
          savedTab={navStack.savedTabForCurrentCompany}
          savedComparablesId={navStack.savedComparablesIdForCurrentCompany}
          isBackNavigation={navStack.isBackNavigation}
          clearBackNavigationFlag={navStack.clearBackNavigationFlag}
          isFromWatchList
          project={{ ownerId: authStore.currentUserId }}
        />
      </StaticPageLayout>
    );
  }

  return (
    <>
      {!!state.isAddRemoveWatchlistPopupOpen && (
        <>
          <div
            className="add-remove-watchlist-popup-closer"
            onClick={() => state.setIsAddRemoveWatchlistPopupOpen()}
          />
          <div className="add-remove-watchlist-popup">
            <div className="title">{`Are you sure you want to remove ${companyToRemove.name} from watchlist?`}</div>
            <div className="buttons-container">
              <IconButton
                innerText="Cancel"
                onClick={() => state.setIsAddRemoveWatchlistPopupOpen()}
              />
              <IconButton
                innerText="Remove from watchlist"
                type={ICON_BUTTON_TYPES.RED}
                filled
                onClick={() => {
                  state.setIsAddRemoveWatchlistPopupOpen();
                  profilerProjectStore.addToWatchlist(companyToRemove);
                }}
              />
            </div>
          </div>
        </>
      )}
      <StaticPageLayout page="profiler-watch-list">
        <div className="watchlist-header">
          <div className="watchlist-title">My watchlist</div>
          {state.layout === 'mobile' ? sortJSX : null}
          <div className="bottom-border" />
        </div>
        <div className="listing-layout">
          <div className="results">
            <div className="results-header">
              <div className="cols">
                <div className="col company-name">Company name</div>
                <div className="col">Location</div>
                <div className="col">Industry</div>
                <div className="col">Value chain</div>
                <div className="col">Products</div>
                <div className="col right-align company-revenue">Sales (€M)</div>
                <div className="col right-align company-employees">Employees</div>
              </div>
              <div className="actions watchlist">{state.layout === 'desktop' ? sortJSX : null}</div>
            </div>

            {showNoResults && <NoResults />}

            {showResults &&
              state.watchlist
                .slice(page * watchlistPagesize, (page + 1) * watchlistPagesize)
                .map((company) => (
                  <WatchlistCompanyResult
                    key={company.id}
                    company={company}
                    displayDetails={navStack.displayDetails}
                    setIsAddRemoveWatchlistPopupOpen={state.setIsAddRemoveWatchlistPopupOpen}
                  />
                ))}

            {/* TD-1787: Show spinner when refreshing with cached data */}
            {profilerProjectStore.isLoadingWatchlist &&
              profilerProjectStore.watchlist.length > 0 && (
                <div className="watchlist-refresh-spinner">
                  <ThreeDots width={48} height={12} color="#348CF9" />
                </div>
              )}

            <Pagination
              show={showPagination}
              onPageChange={(selected) => {
                navigate(
                  paths.PROFILER_WATCH_LIST +
                    '?params=' +
                    encodeWatchlistParams({ ...state.params, page: selected + 1 }),
                  { replace: true }
                );
                setTimeout(() => {
                  if (state.layout === 'mobile') {
                    window.scrollTo({ top: 16, behavior: 'smooth' });
                    return;
                  }

                  window.scrollTo({ top: 24 + 23, behavior: 'smooth' });
                }, 100);
              }}
              page={page}
              pages={pages}
            />
          </div>
        </div>
      </StaticPageLayout>
    </>
  );
});

export default ProfilerWatchList;
